import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Tooltip,
  Autocomplete,
  TextField,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import {
  SelectInputComponent,
  SearchInputComponent,
} from "../../../../../../common/components";
import DatePickerComponent from "../../../../../../common/components/date-picker/date-picker-compoenent";
import CloseIcon from "../../../../../../assets/icons/closeIcon.svg";
import ownerImage from "../../../../../../assets/icons/userProfileIcone.png";
import {
  addMilestones,
  updateMilestones,
} from "../../../../../../api/milestones";
import "./company-dashboard-initiative-drawer-add-milestones.scss";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { debounce } from "lodash";
import { getMilestoneTitle } from "../../../../../../api/title-search";

const CompanyDashboardInitiativeDrawerAddMilestones: any = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    addPopupTitle,
    milestonesData,
    editData,
    employeesList,
    setLoading,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    initiativeId,
    fetchInitiativeById,
    keyResultId,
    objectiveId,
    initiativeAPIData,
    fetchAllMilestones,
    departmentsList,
    fetchMilestoneDepartments,
  } = props;
  const statusOptions = [
    { value: "Created", label: "Created" },
    { value: "Missed", label: "Missed" },
    { value: "Achieved", label: "Achieved" },
  ];

  const subDepartmentList = [
    { color: "#FCE8D2", text: "Sales", id: [1, 11, 21, 31, 41, 51] },
    { color: "#D6ECD2", text: "Marketing", id: [2, 12, 22, 32, 42, 52] },
    { color: "#FFD9E7", text: "AI Team", id: [3, 13, 23, 33, 43, 53] },
    { color: "#DEC3A6", text: "Finance", id: [4, 14, 24, 34, 44, 54] },
    { color: "#DDEAFF", text: "Accounting", id: [5, 15, 25, 35, 45, 55] },
    { color: "#CAC3D6", text: "IT", id: [6, 16, 26, 36, 46, 56] },
    {
      color: "#C3E0DF",
      text: "Engineering & Transformation",
      id: [7, 17, 27, 37, 47, 57],
    },
    { color: "#BAD1B6", text: "PMO", id: [8, 18, 28, 38, 48, 58] },
    { color: "#FFFDD9", text: "Data", id: [9, 19, 29, 39, 49, 59] },
    {
      color: "#E0C3CE",
      text: "Technology and Data",
      id: [10, 20, 30, 40, 50, 60],
    },
  ];
  const mainDepartmentsList =
    departmentsList?.map((dept: any) => ({
      color:
        subDepartmentList?.find((d: any) => d.id.includes(dept.departmentId))
          ?.color || "#E8F0FE",
      text: dept.departmentName,
      id: dept.departmentId,
    })) || [];
  const [selectedNames, setSelectedNames] = useState<any[]>([]);
  const [selectedDepartmentList, setSelectedDepartmentList] = useState<any[]>(
    [],
  );
  const [selectedRole, setSelectedRole] = useState<any[]>([]);
  const [mainDepartments, setMainDepartments] = useState<any[]>([]);
  const [errorsMessage, setErrorsMessage] = useState<any>("");
  const [open, setOpen] = useState(false);
  const [filteredDepartments, setFilteredDepartments] = useState<any[]>([]);
  const [titleSuggestions, setTitleSuggestions] = useState<any[]>([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  // Clear search state when popup closes
  React.useEffect(() => {
    if (!isOpenAddPopup) {
      setTitleSuggestions([]);
      setHasSearched(false);
      setAutocompleteOpen(false);
      setLoadingTitles(false);
    }
  }, [isOpenAddPopup]);

  // Debounced function to search for initiative titles
  const searchMilestoneTitles = React.useMemo(
    () =>
      debounce(async (searchValue: string) => {
        if (!searchValue || searchValue.trim().length < 2) {
          setTitleSuggestions([]);
          setHasSearched(false);
          return;
        }

        setLoadingTitles(true);
        setHasSearched(true);
        try {
          const response = await getMilestoneTitle({ title: searchValue });
          const milestones = response?.data?.data || [];
          setTitleSuggestions(milestones);
        } catch (error: any) {
          setTitleSuggestions([]);
          console.error("Error fetching milestone titles:", error);
        } finally {
          setLoadingTitles(false);
        }
      }, 500),
    [],
  );
  useEffect(() => {
    const updatedMainDepartments = initiativeAPIData?.departments
      ?.map((dept: any) =>
        mainDepartmentsList?.find((d: any) => d.id === dept?.departmentId),
      )
      ?.filter((emp: any) => emp !== undefined);

    const emp = employeesList?.find(
      (data: any) =>
        editData?.data?.employeeDetailResponse?.employeeId === data?.employeeId,
    );

    setSelectedRole(
      emp
        ? [
            {
              id: emp?.employeeId,
              name: emp?.employeeFullName,
              label: emp?.employeeFullName,
              profileImage: emp?.employeeImage || ownerImage,
              role: emp?.roleName,
              roleId: emp?.roleId,
            },
          ]
        : [],
    );

    if (milestonesData?.length > 0) {
      setSelectedNames(
        milestonesData
          ?.filter((milestone: any) => milestone.id !== editData?.id)
          ?.map((milestone: any) => milestone.milestone?.toLowerCase()) || [],
      );
      setSelectedDepartmentList(
        milestonesData
          ?.filter((milestone: any) => milestone.id !== editData?.id)
          ?.map(
            (milestone: any) =>
              milestone?.data?.departmentResponse?.departmentId,
          ) || [],
      );
    }
    setFilteredDepartments(updatedMainDepartments || []);
    setMainDepartments(updatedMainDepartments);
  }, [employeesList, editData]);

  const handleCreateMilestone = async (payload: any) => {
    setLoading(true);
    const isDepartments = selectedDepartmentList?.includes(
      editData?.id ? payload?.departmentId : payload[0]?.departmentId,
    );
    const isNames = selectedNames?.includes(
      editData?.id
        ? payload?.title?.toLowerCase()
        : payload[0]?.title?.toLowerCase(),
    );

    if (isDepartments && isNames) {
      setLoading(false);
      setErrorsMessage(
        "Milestone with same name and department already exists.",
      );
      return;
    }
    try {
      await (editData?.id
        ? updateMilestones(editData?.id, payload)
        : addMilestones(payload));
      fetchInitiativeById();
      if (editData?.id) {
        setAlertMessage("Initiative updated successfully");
      } else {
        setAlertMessage("Initiative created successfully");
      }
      fetchAllMilestones();
      fetchMilestoneDepartments(initiativeId);
      // Refresh the upcoming milestones calendar
      // if (fetchUpcomingMilestones) {
      //   fetchUpcomingMilestones({});
      // }

      setAlertType("success");
      setAlertOpen(true);
      setLoading(false);
      onCancel();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create Initiative",
      );

      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const validationSchema = () =>
    Yup.object({
      milestone: Yup.string().required("Milestone is required"),
      // assignedTo: Yup.array()
      //   .of(Yup.string().required("Assign To is required"))
      //   .min(1, "Assign To is required"),
      participatingDepartments: Yup.array()
        .of(Yup.string().required("Participating Department is required"))
        .min(1, "Participating Department is required"),
      date: Yup.date().nullable().required("Start Date is required"),
      // status: Yup.array()
      //   .of(Yup.string().trim().required("Status is required"))
      //   .nullable(),
    });
  const handleDepartmentOptionsUpdate = () => {
    setSelectedRole([]);
  };

  const validateFormData = (validateForm: any) => {
    setTimeout(() => {
      validateForm();
    }, 0);
  };

  const getStatusId = (status: string): number => {
    switch (status) {
      case "Created":
        return 31;
      case "Missed":
        return 32;
      case "Achieved":
        return 33;
      default:
        return 31;
    }
  };
  const slotPaperTitle = (props: any, values: any) => {
    const showNoResults =
      !loadingTitles &&
      hasSearched &&
      titleSuggestions.length === 0 &&
      values.milestone.trim().length >= 2;

    const showLoading = loadingTitles;
    const renderContent = () => {
      if (showLoading) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Searching...
          </div>
        );
      }
      if (showNoResults) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            No results found.
          </div>
        );
      }
      if (titleSuggestions.length === 0) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Search
          </div>
        );
      }
      return props.children;
    };

    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "6px 16px",
            background: "#fff",
            borderBottom: "0.5px solid #eee",
          }}
        >
          <div className="" style={{ fontSize: "14px" }}>
            <InputLabel style={{ fontSize: "14px" }}>
              Existing Milestones
            </InputLabel>
          </div>
          <button
            style={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setTitleSuggestions([]);
              setHasSearched(false);
              setAutocompleteOpen(false);
            }}
          >
            <img
              alt="Close"
              src={CloseIcon}
              style={{
                cursor: "pointer",
                color: "#555",
                margin: "4px 0",
              }}
            />
          </button>
        </div>
        {renderContent()}
      </div>
    );
  };

  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-initiative-drawer-add-disposition`}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: "#f7f8fb",
        },
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">{addPopupTitle}</div>
          <div className="image-wrapper" style={{ cursor: "pointer" }}>
            <button
              onClick={onCancel}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <img src={CloseIcon} className="close-icon" alt="close" />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Formik
            initialValues={{
              milestone: editData?.milestone || "",
              date: dayjs(editData?.date) || null,
              status: editData?.data?.statusResponse?.statusName || "",
              participatingDepartments:
                editData?.data?.departmentResponse?.departmentName ||
                editData?.data?.departmentName
                  ? [
                      editData?.data?.departmentResponse?.departmentName ||
                        editData?.data?.departmentName,
                    ]
                  : [],
              // assignedTo: selectedRole?.map((role) => role.name) || [],
              assignedTo:
                employeesList?.find((emp: any) => emp?.emailId === "")
                  ?.employeeId || [],
            }}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize={false}
            onSubmit={(values) => {// NOSONAR
              handleCreateMilestone(
                editData?.id
                  ? {
                      departmentId:
                        mainDepartments?.find(
                          (dept: any) =>
                            dept.text === values?.participatingDepartments[0],
                        )?.id || null,
                      description: values?.milestone || "",
                      employeeId: selectedRole?.[0]?.id || null,
                      endDate: values?.date
                        ? dayjs(values?.date).format("YYYY-MM-DD")
                        : null,
                      title: values?.milestone || "",
                      yearNum: 2025,
                      statusId: getStatusId(values?.status),
                    }
                  : [
                      {
                        departmentId:
                          mainDepartments?.find(
                            (dept: any) =>
                              dept.text === values?.participatingDepartments[0],
                          )?.id || null,
                        description: values?.milestone || "",
                        endDate: values?.date
                          ? dayjs(values?.date).format("YYYY-MM-DD")
                          : null,
                        initivateId: initiativeId,
                        keyResultId: keyResultId,
                        objectiveId: Number(objectiveId),
                        title: values?.milestone || "",
                        yearNum: 2025,
                        statusId: getStatusId(values?.status),
                      },
                    ],
              );
            }}
          >
            {(
              {
                values,
                setFieldValue,
                touched,
                errors,
                setFieldError,
                isValid,
                submitCount,
                validateForm,
              }) => (// NOSONAR
              <Form>
                <div className="create-lead-capture-popup-confirmation-description-checkbox-input">
                  <div className="form-group form-text-input-component">
                    <InputLabel>Title</InputLabel>
                    <Autocomplete
                      freeSolo
                      options={titleSuggestions}
                      getOptionLabel={(option: any) =>
                        typeof option === "string" ? option : option.title || ""
                      }
                      loading={loadingTitles}
                      filterOptions={(x) => x}
                      open={autocompleteOpen}
                      onOpen={() => {
                        // Only open if there's meaningful content to show
                        if (
                          values.milestone.trim().length >= 2 ||
                          titleSuggestions.length > 0 ||
                          loadingTitles
                        ) {
                          setAutocompleteOpen(true);
                        }
                      }}
                      onClose={(event, reason) => {
                        // Always close on blur (clicking outside) or escape key
                        if (reason === "blur" || reason === "escape") {
                          setAutocompleteOpen(false);
                          return;
                        }

                        // For other reasons, keep open if there are results, loading, or no results after search
                        const shouldStayOpen =
                          titleSuggestions.length > 0 ||
                          loadingTitles ||
                          (hasSearched &&
                            !loadingTitles &&
                            values.milestone.trim().length >= 2);

                        // Only close if shouldn't stay open (not when showing no results)
                        if (!shouldStayOpen) {
                          setAutocompleteOpen(false);
                        }
                      }}
                      disableClearable
                      value={values.milestone}
                      inputValue={values.milestone}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setFieldValue("milestone", newInputValue);
                          searchMilestoneTitles(newInputValue);
                          // Open dropdown when typing with enough characters
                          if (newInputValue.trim().length >= 2) {
                            setAutocompleteOpen(true);
                          } else {
                            // Close dropdown when text is cleared or too short
                            setAutocompleteOpen(false);
                            setTitleSuggestions([]);
                            setHasSearched(false);
                          }
                        }
                      }}
                      onChange={(event) => {
                        // Prevent selection - only allow typing
                        event.preventDefault();
                      }}
                      onBlur={() => {
                        // Only clear if we're not showing no results message
                        setTimeout(() => {
                          const shouldKeepOpen =
                            titleSuggestions.length > 0 ||
                            loadingTitles ||
                            (hasSearched &&
                              !loadingTitles &&
                              values.milestone.trim().length >= 2);

                          if (!shouldKeepOpen) {
                            setTitleSuggestions([]);
                            setHasSearched(false);
                            setAutocompleteOpen(false);
                          }
                        }, 200);
                        if (submitCount > 0) {
                          setTimeout(() => {
                            validateForm();
                          }, 0);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Enter Title"
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              maxLength: 150,
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option: any) => (
                        <Tooltip title="Selection is turned off for this field.">
                          <li
                            {...props}
                            style={{
                              cursor: "not-allowed",
                              padding: "8px 16px",
                              opacity: 0.7,
                              backgroundColor:
                                values?.milestone?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                values?.milestone?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                values?.milestone?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important";
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                pointerEvents: "none",
                              }}
                            >
                              {option.title}
                            </div>
                          </li>
                        </Tooltip>
                      )}
                      noOptionsText={(() => {
                        if (loadingTitles) return "Searching...";
                        if (values.milestone.length < 2)
                          return "Type at least 2 characters";
                        return "No results found.";
                      })()}
                      slotProps={{
                        listbox: {
                          style: {
                            maxHeight: "200px",
                          },
                        },
                      }}
                      slots={{
                        paper: (props) => slotPaperTitle(props, values),
                      }}
                    />
                    <ErrorMessage
                      name="milestone"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <div className="form-group">
                    <div
                      className={`form-text-input-component content-milestones-participating-departments-dropdown`}
                    >
                      <InputLabel id="participating-department(s)">
                        Participating Department(s)
                      </InputLabel>

                      <FormControl
                        variant="standard"
                        sx={{ minWidth: 120, width: "100%" }}
                      >
                        <Select
                          open={open}
                          onOpen={() => setOpen(true)}
                          onClose={() => {
                            setOpen(false);
                            if (
                              typeof setFieldError === "function" &&
                              values.participatingDepartments.length > 0
                            ) {
                              setFieldError("participatingDepartments", "");
                            }
                            if (submitCount > 0) {
                              validateFormData(validateForm);
                            }
                          }}
                          labelId="participating-department(s)"
                          input={<OutlinedInput label="Tag" />}
                          id="participating-department(s)"
                          value={values.participatingDepartments}
                          multiple
                          renderValue={(selected: any) => {
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {selected?.map((value: any) => (
                                  <Chip
                                    key={value}
                                    sx={{
                                      backgroundColor: mainDepartments?.find(
                                        (dept: any) =>// NOSONAR
                                         dept.text === value,
                                      )?.color,
                                      paddingLeft: "2px",
                                      paddingRight: "2px",
                                    }}
                                    deleteIcon={
                                      <img
                                        src={CloseIcon}
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                        }}
                                        alt="close"
                                      />
                                    }
                                    label={value}
                                  />
                                ))}
                              </Box>
                            );
                          }}
                          MenuProps={{
                            slotProps: {
                              paper: {
                                style: {
                                  maxHeight: 48 * 4.5,
                                  width: 250,
                                  padding: 0,
                                },
                              },
                            },
                          }}
                          label=""
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              padding: "4px 8px",
                              backgroundColor: "#fff",
                            }}
                          >
                            <button
                              style={{
                                border: "none",
                                background: "none",
                                padding: 0,
                                cursor: "pointer",
                              }}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (typeof setFieldError === "function") {
                                  setFieldError("participatingDepartments", "");
                                }
                                setOpen(false);
                              }}
                            >
                              <img
                                alt="Close"
                                src={CloseIcon}
                                style={{
                                  cursor: "pointer",
                                }}
                              />
                            </button>
                          </div>
                          <MenuItem
                            value=""
                            className="company-dashboard-initiative-drawer-side-bar-content-milestones-edit-section "
                            disableRipple
                            sx={{
                              "&:hover": {
                                backgroundColor: "transparent !important",
                              },
                              "&.Mui-focusVisible": {
                                backgroundColor: "transparent !important",
                              },
                            }}
                          >
                            <OverlayScrollbarsComponent
                              style={{ height: "300px" }}
                              className="company-dashboard-initiative-drawer-side-bar-content-milestones-edit"
                              options={{
                                scrollbars: {
                                  theme: "os-theme-dark",
                                  autoHide: "scroll",
                                  autoHideDelay: 400,
                                },
                              }}
                            >
                              <div className="company-dashboard-initiative-drawer-side-bar-content-milestones-edit-search">
                                <SearchInputComponent
                                  placeholder="Search Departments"
                                  onSearch={(value: string) => {
                                    if (value?.trim()) {
                                      const filtered = mainDepartments?.filter(
                                        (dept: any) =>
                                          dept.text
                                            .toLowerCase()
                                            .includes(value.toLowerCase()),
                                      );
                                      setFilteredDepartments(filtered);
                                    } else {
                                      setFilteredDepartments(mainDepartments);
                                    }
                                  }}
                                  isReset={false}
                                  ref={null}
                                />
                              </div>
                              <div className="company-dashboard-initiative-drawer-side-bar-content-milestones-content">
                                {filteredDepartments?.map(
                                  (department: any, index: number) => (
                                    <button
                                      type="button"
                                      className="company-dashboard-initiative-drawer-side-bar-content-milestones-content-item"
                                      key={index + 1}
                                      style={{
                                        backgroundColor:
                                          department?.color || "",
                                      }}
                                      onClick={() => {
                                        if (
                                          !values.participatingDepartments.includes(
                                            department.text,
                                          )
                                        ) {
                                          setFieldValue(
                                            "participatingDepartments",
                                            [department.text],
                                          );
                                          if (
                                            typeof setFieldError === "function"
                                          ) {
                                            setFieldError(
                                              "participatingDepartments",
                                              "",
                                            );
                                          }
                                          setErrorsMessage("");
                                          setFieldValue("assignedTo", []);
                                          // Trigger validation if form has been submitted before
                                          // if (touched.assignedTo) {
                                          //   setTimeout(() => {
                                          //     validateField("assignedTo");
                                          //   }, 0);
                                          // }
                                          handleDepartmentOptionsUpdate();
                                        }
                                        if (submitCount > 0) {
                                          validateFormData(validateForm);
                                        }
                                      }}
                                    >
                                      <div className="company-dashboard-initiative-drawer-side-bar-content-milestones-content-item-label">
                                        {department?.text}{" "}
                                      </div>{" "}
                                      <div className="company-dashboard-initiative-drawer-side-bar-content-milestones-content-item-img">
                                        {values.participatingDepartments?.find(
                                          (dept: any) =>
                                            dept === department.text,
                                        ) ? (
                                          <img src={CloseIcon} alt="close" />
                                        ) : (
                                          <span> + </span>
                                        )}
                                      </div>
                                    </button>
                                  ),
                                )}
                                {(() => {
                                  if (mainDepartments?.length === 0) {
                                    return (
                                      <div
                                        className="no-data-found"
                                        style={{
                                          textAlign: "center",
                                          width: "100%",
                                        }}
                                      >
                                        Participating Department(s) Not Found.
                                        <br />
                                        Please add Departments.
                                      </div>
                                    );
                                  }
                                  if (filteredDepartments?.length === 0) {
                                    return (
                                      <div
                                        className="no-data-found"
                                        style={{
                                          textAlign: "center",
                                          width: "100%",
                                        }}
                                      >
                                        Participating Department(s) Not Found.
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </OverlayScrollbarsComponent>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    <div className="error-message">
                      {typeof errors?.participatingDepartments === "string"
                        ? errors?.participatingDepartments || ""
                        : null}
                    </div>
                  </div>

                  <div className="form-group">
                    <DatePickerComponent
                      fieldName={"date"}
                      label="Date"
                      value={values?.date}
                      onChange={(e: any) => {
                        setFieldValue("date", e);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <SelectInputComponent
                      fieldName="status"
                      placeholder="Select Status"
                      label="Status"
                      optional={false}
                      disabled={addPopupTitle !== "Edit Milestone"}
                      options={statusOptions}
                      value={values.status || "Created"}
                      onChange={(e: any) => setFieldValue("status", e)}
                      multiple={false}
                    />
                    {touched?.status && (
                      <ErrorMessage
                        name={"status"}
                        component="div"
                        className="error-message"
                      />
                    )}
                  </div>
                </div>
                <div className="error-message">
                  {typeof errors === "string" ? errors || "" : null}
                </div>
                <DialogActions>
                  <div className="action-butn-wrapper">
                    <button
                      onClick={onCancel}
                      className="cancel-btn"
                      type="button"
                    >
                      Cancel
                    </button>
                    {errorsMessage || (submitCount > 0 && !isValid) ? (
                      <Tooltip
                        title={
                          errorsMessage ||
                          "Please resolve all errors before submitting."
                        }
                        placement="top"
                      >
                        <span>
                          <button
                            autoFocus
                            className="action-btn"
                            type="submit"
                            disabled
                            style={{
                              opacity: 0.5,
                              cursor: "not-allowed",
                            }}
                          >
                            {addPopupTitle === "Edit Milestone"
                              ? "Save"
                              : "Create"}
                          </button>
                        </span>
                      </Tooltip>
                    ) : (
                      <button
                        autoFocus
                        className="action-btn"
                        type="submit"
                        style={{
                          opacity: 1,
                          cursor: "pointer",
                        }}
                      >
                        {addPopupTitle === "Edit Milestone" ? "Save" : "Create"}
                      </button>
                    )}
                  </div>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContentText>
      </DialogContent>
    </Dialog>
    // </div>
  );
};

export default CompanyDashboardInitiativeDrawerAddMilestones;
